import {
	getEmailTemplate,
	listEmailTemplates,
	previewEmailTemplate,
	resetEmailTemplate,
	updateEmailTemplate,
} from '@dealstack/frontend/shared/components/EmailTemplates/services/emailTemplates.service';
import type { EmailTemplateScope } from '@dealstack/frontend/shared/components/EmailTemplates/EmailTemplates.types';
import { getBackendErrorMessage } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { suppressMutationErrorToast } from '@dealstack/frontend/shared/dataExchange/reactQuery/queryErrorHandler';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { PreviewEmailTemplateDto, UpdateEmailTemplateDto } from '@dealstack/shared/lib/generated';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const LIST_QUERY_KEY = 'emailTemplates';
const DETAIL_QUERY_KEY = 'emailTemplate';

export const useEmailTemplatesList = ({ scope }: { scope: EmailTemplateScope }) =>
	useQuery({
		queryKey: [LIST_QUERY_KEY, scope.kind, scope.id],
		queryFn: () => listEmailTemplates(scope),
		select: (res) => res.payload ?? [],
	});

export const useEmailTemplate = ({
	scope,
	type,
	enabled = true,
}: {
	scope: EmailTemplateScope;
	type: string;
	enabled?: boolean;
}) =>
	useQuery({
		queryKey: [DETAIL_QUERY_KEY, scope.kind, scope.id, type],
		queryFn: () => getEmailTemplate(scope, type),
		select: (res) => res.payload,
		enabled: enabled && Boolean(type),
	});

const invalidateTemplate = (
	queryClient: ReturnType<typeof useQueryClient>,
	scope: EmailTemplateScope,
	type: string
) =>
	Promise.all([
		queryClient.invalidateQueries({ queryKey: [LIST_QUERY_KEY, scope.kind, scope.id] }),
		queryClient.invalidateQueries({ queryKey: [DETAIL_QUERY_KEY, scope.kind, scope.id, type] }),
	]);

export const useUpdateEmailTemplate = ({ scope }: { scope: EmailTemplateScope }) => {
	const queryClient = useQueryClient();
	const tMipManage = useTranslationMipManage();

	return useMutation({
		mutationFn: (args: {
			type: string;
			body: UpdateEmailTemplateDto;
			successMessage?: string;
		}) => updateEmailTemplate(scope, args.type, args.body),
		onSuccess: async (_res, args) => {
			showToastV2({
				state: 'success',
				message: args.successMessage ?? tMipManage('email_templates.toast.saved'),
			});
			await invalidateTemplate(queryClient, scope, args.type);
		},
		onError: (e) => {
			showToastV2({
				state: 'error',
				message:
					getBackendErrorMessage(e) ?? tMipManage('email_templates.toast.save_error'),
			});
		},
	});
};

export const useResetEmailTemplate = ({ scope }: { scope: EmailTemplateScope }) => {
	const queryClient = useQueryClient();
	const tMipManage = useTranslationMipManage();

	return useMutation({
		mutationFn: (args: { type: string }) => resetEmailTemplate(scope, args.type),
		onSuccess: async (_res, args) => {
			showToastV2({
				state: 'success',
				message: tMipManage('email_templates.toast.reset_success'),
			});
			await invalidateTemplate(queryClient, scope, args.type);
		},
		onError: (e) => {
			showToastV2({
				state: 'error',
				message:
					getBackendErrorMessage(e) ?? tMipManage('email_templates.toast.reset_error'),
			});
		},
	});
};

export const usePreviewEmailTemplate = ({ scope }: { scope: EmailTemplateScope }) =>
	useMutation({
		mutationFn: (args: { type: string; body: PreviewEmailTemplateDto }) =>
			previewEmailTemplate(scope, args.type, args.body),
		onError: suppressMutationErrorToast,
	});
